import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  GSSidePanelPage,
  GSActionBar,
  GSButton,
  GSDivider,
  GSEmptyList,
  GSFormSection,
  GSInput,
  GSItemList,
  defaultBorders,
  defaultButtonStyles,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  useCollapsable,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faFileInvoiceDollar,
  faFlag,
  faSearch,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useInlineResponseEditing } from "../../../hooks/customFieldResponseHooks";
import {
  slideoutContentStyle,
  slideoutPageStyle,
  slideoutRootStyle,
} from "../../../templates/slideout-styles";
import {
  getOrderFormResponses,
  clearCurrentOrderFormResponses,
  clearNotifications,
  selectCurrentOrderFormResponses,
  selectCustomFieldResponsesLoading,
} from "../../../reducers/customFieldResponsesSlice";
import {
  getResponseNodes,
  filterResponseNodes,
  getAvailableResponseTypeOptions,
  getResponsePlayers,
  getResponseTeams,
  getResponseSponsors,
  RESPONSE_TYPE_LABELS,
} from "../../../helpers/CustomFieldResponses";
import TournamentOrderFormResponseCard from "./tournament-order-form-response-card";

const teamRecordTypes = ["Team", "TournamentTeam"];

const orderViewActions = {
  Team: { title: "View Team", buttonIcon: faUsers, base: "teams" },
  TournamentTeam: { title: "View Team", buttonIcon: faUsers, base: "teams" },
  Sponsor: { title: "View Sponsor", buttonIcon: faFlag, base: "sponsors" },
  Order: {
    title: "View Order",
    buttonIcon: faFileInvoiceDollar,
    base: "reporting",
  },
};

const sectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad.apply("horizontal"),
  ...defaultPadding.xLargePad.apply("vertical"),
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
};

const filterSectionStyle = {
  ...sectionStyle,
  ...defaultPadding.xLargePad.apply("vertical"),
};

const searchSectionStyle = {
  ...sectionStyle,
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: golfstatusColors.white,
};

const bodySectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
};

const emptySectionStyle = {
  ...defaultPadding.largeLayoutPad.apply("vertical"),
  ...defaultPadding.xLargePad.apply("horizontal"),
};


const responseGroupStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumLargeGap,
};

const cardWrapperStyle = {
  ...defaultLayouts.fullWidth,
};

const packageLabelStyle = {
  ...defaultTypography.overline2,
  ...defaultTypography.uppercase,
  color: golfstatusColors.grey500,
};

const pillStyle = { ...defaultBorders.mediumLargeBorderRadius };

const responseTypeHeaderStyle = {
  ...defaultLayouts.vStack,
};

const scopeRowStyle = {
  ...defaultLayouts.hStack,
  ...defaultGapping.mediumGap,
  alignItems: "center",
};

const scopeLabelStyle = {
  ...defaultTypography.bodyRegular,
  color: golfstatusColors.grey800,
  opacity: 0.7,
};

const dividerStyle = {
  width: "1px",
  height: defaultTypography.bodyRegular.fontSize,
  backgroundColor: golfstatusColors.grey200,
};

const filterPillsRowStyle = {
  ...defaultLayouts.hStack,
  ...defaultGapping.mediumGap,
  flexWrap: "wrap",
};

const TournamentOrderFormResponses = () => {
  const match = useMatch("/:tournamentID/reporting/:orderID/form-responses");
  const orderID = match?.params?.orderID;
  const tournamentID = match?.params?.tournamentID;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (orderID) {
      dispatch(getOrderFormResponses({ key: orderID }));
    }
    return () => {
      dispatch(clearCurrentOrderFormResponses());
      dispatch(clearNotifications());
    };
  }, [dispatch, orderID]);

  const payload = useSelector(selectCurrentOrderFormResponses);
  const loading = useSelector(selectCustomFieldResponsesLoading);

  const [scope, setScope] = useState("all");
  const [, toggleHide, , , , setHide, hide, content] = useCollapsable(true);
  const [search, setSearch] = useState("");
  const [drill, setDrill] = useState(null);
  const [subSelection, setSubSelection] = useState({ type: null, id: null });
  const {
    banner,
    editingKey,
    requestEdit,
    commitRef,
    setPendingEdit,
    leftNavigation,
  } = useInlineResponseEditing(() => navigate(-1));

  const responseGroups = filterResponseNodes(getResponseNodes(payload), {
    scope,
    search,
    player: subSelection.type === "player" ? subSelection.id : null,
    team: subSelection.type === "team" ? subSelection.id : null,
    sponsor: subSelection.type === "sponsor" ? subSelection.id : null,
  });
  const responseTypeOptions = getAvailableResponseTypeOptions(payload);
  const players = getResponsePlayers(payload);
  const teams = getResponseTeams(payload);
  const sponsors = getResponseSponsors(payload);

  const refetch = () => {
    if (orderID) {
      dispatch(getOrderFormResponses({ key: orderID }));
    }
  };

  const getNavigation = () => ({
    title: "Form Responses",
    leftIcon: faChevronLeft,
    leftButtonClick: leftNavigation,
  });

  const selectScope = (key) => {
    setScope(key);
    setSubSelection({ type: null, id: null });
    const needsDrill = key === "player" || key === "team" || key === "sponsor";
    if (needsDrill) {
      setDrill(key);
      return;
    }
    setDrill(null);
    setHide(true);
  };

  const selectSubItem = (type, id) => {
    setSubSelection({ type, id });
    setHide(true);
  };

  const exitDrill = () => {
    setSubSelection({ type: null, id: null });
    setDrill(null);
  };

  const isFiltering =
    search.trim() !== "" || scope !== "all" || subSelection.id != null;

  const clearAll = () => {
    setSearch("");
    setScope("all");
    setSubSelection({ type: null, id: null });
    setDrill(null);
  };

  const getDrillItems = () => {
    if (drill === "team") {
      return teams;
    }
    if (drill === "sponsor") {
      return sponsors;
    }
    return players;
  };

  const getViewTarget = (responseGroup) => {
    const record = responseGroup?.record;
    const config = orderViewActions[record?.type];
    if (!config || record?.id == null) {
      return null;
    }
    return {
      title: config.title,
      buttonIcon: config.buttonIcon,
      onClick: () => navigate(`/${tournamentID}/${config.base}/${record.id}`),
    };
  };

  const getFormViewAction = (responseGroup, form) => {
    if (
      form?.scope === "player" &&
      teamRecordTypes.includes(responseGroup?.record?.type)
    ) {
      return {
        title: "View Team",
        buttonIcon: faUsers,
        onClick: () =>
          navigate(`/${tournamentID}/teams/${responseGroup?.record?.id}`),
      };
    }
    return getViewTarget(responseGroup);
  };

  const getScopeLabel = () => {
    if (subSelection.id == null) {
      return RESPONSE_TYPE_LABELS[scope];
    }
    const selected = getDrillItems().find((item) => item.id === subSelection.id);
    if (!selected?.name) {
      return RESPONSE_TYPE_LABELS[scope];
    }
    return `${selected.name} Responses`;
  };

  const getResponseTypeHeader = () => (
    <div style={responseTypeHeaderStyle}>
      <div style={{ ...defaultTypography.headline3 }}>Form Responses</div>
      <div style={scopeRowStyle}>
        <div style={scopeLabelStyle}>{getScopeLabel()}</div>
        <GSDivider style={dividerStyle} />
        <GSButton
          {...defaultButtonStyles.text.withStyle({
            color: golfstatusColors.cyan600,
            letterSpacing: "normal",
            fontSize: defaultTypography.bodyRegular.fontSize,
            userSelect: "none",
          })}
          title={!hide ? "Close" : "Change"}
          onClick={() => {
            toggleHide();
          }}
        />
      </div>
    </div>
  );

  const getScopePills = () =>
    responseTypeOptions.map((option) => (
      <GSButton
        key={option.key}
        type={scope === option.key ? "black pill" : "light-grey pill"}
        title={option.label}
        style={pillStyle}
        onClick={() => selectScope(option.key)}
      />
    ));

  const getDrillPills = () => (
    <>
      <GSButton
        type="light-grey pill"
        buttonIcon={faChevronLeft}
        style={pillStyle}
        onClick={exitDrill}
      />
      {getDrillItems().map((item) => (
        <GSButton
          key={item.id}
          type={
            subSelection.type === drill && subSelection.id === item.id
              ? "black pill"
              : "light-grey pill"
          }
          title={item.name}
          style={pillStyle}
          onClick={() => selectSubItem(drill, item.id)}
        />
      ))}
    </>
  );

  const getResponseTypeFilterSection = () => (
    <div style={filterSectionStyle}>
      <div style={{ ...defaultTypography.overline2 }}>RESPONSE TYPE</div>
      <div style={filterPillsRowStyle}>
        {drill ? getDrillPills() : getScopePills()}
      </div>
    </div>
  );

  const getSearchSection = () => (
    <div style={searchSectionStyle}>
      <GSInput
        textValue={search}
        placeholder="Search..."
        leftIcon={faSearch}
        onChange={(event) => setSearch(event.target.value)}
      />
    </div>
  );

  const getFormCard = (responseGroup, form) => (
    <TournamentOrderFormResponseCard
      key={form?.form?.id}
      form={form}
      viewAction={getFormViewAction(responseGroup, form)}
      onSaved={refetch}
      onUnsaved={setPendingEdit}
      editingKey={editingKey}
      onEdit={requestEdit}
      commitRef={commitRef}
    />
  );

  const getFormCardList = (responseGroup) => (
    <GSItemList
      type="vertical x-large-gap"
      style={{ ...defaultLayouts.fullWidth }}
      items={responseGroup?.forms ?? []}
      listItem={(form) => (
        <div style={cardWrapperStyle} key={form?.form?.id}>
          {getFormCard(responseGroup, form)}
        </div>
      )}
    ></GSItemList>
  );

  const getResponseGroup = (responseGroup) => (
    <GSFormSection
      key={`${responseGroup?.record?.type}-${responseGroup?.record?.id}`}
      extras={
        <div style={responseGroupStyle}>
          {responseGroup?.record?.type !== "Order" && (
            <div style={packageLabelStyle}>{responseGroup?.package?.name}</div>
          )}
          {getFormCardList(responseGroup)}
        </div>
      }
    ></GSFormSection>
  );

  const getBodySection = () => (
    <div style={bodySectionStyle}>
      {responseGroups.length > 0 ? (
        responseGroups.map((responseGroup) => getResponseGroup(responseGroup))
      ) : (
        <GSEmptyList
          style={emptySectionStyle}
          title={
            isFiltering
              ? "We couldn't find the response you're looking for..."
              : "No Responses Found"
          }
          detail={
            isFiltering
              ? "Try changing the search term and filter."
              : "There are no form responses for this order yet."
          }
          actions={
            isFiltering
              ? [{ title: "Clear All", type: "grey", onClick: clearAll }]
              : []
          }
        />
      )}
    </div>
  );

  const getResponseTypeSection = () => (
    <div style={sectionStyle}>
      <GSActionBar header={getResponseTypeHeader()} />
    </div>
  );

  const getContent = () => (
    <div style={slideoutContentStyle}>
      {getResponseTypeSection()}
      {content(getResponseTypeFilterSection())}
      {getSearchSection()}
      {getBodySection()}
    </div>
  );

  return (
    <tournament-order-form-responses style={slideoutRootStyle}>
      <GSSidePanelPage
        header={getNavigation()}
        loading={loading?.includes?.("getOrderFormResponses") && !payload?.record}
        banner={banner}
        content={getContent()}
        style={slideoutPageStyle}
      ></GSSidePanelPage>
    </tournament-order-form-responses>
  );
};

export default TournamentOrderFormResponses;
